/**
 * PawpStarz / Horizon Product Personalization
 * ------------------------------------------------------------
 * - Conditional Art Format / Frame Color / Size logic
 * - Keeps Horizon's native variant picker + add-to-cart behavior
 * - Injects required customer pet-photo upload + optional notes
 * - Uses Shopify native file-valued line item properties
 * - No external dependencies
 */

(() => {
  'use strict';

  const CONFIG = {
    optionNames: {
      format: 'Art Format',
      frame: 'Frame Color',
      size: 'Size',
    },

    defaults: {
      format: 'Premium Framed Canvas',
      frame: 'Dark Wood',
      size: '12x16"',
    },

    rules: {
      'Premium Framed Canvas': {
        frames: ['Dark Wood', 'Natural Wood', 'Black'],
        sizes: ['12x16"', '18x24"', '24x32"'],
      },
      'Wood Framed Poster': {
        frames: ['Dark Wood', 'Natural Wood', 'Black', 'White'],
        sizes: ['12x16"', '18x24"', '24x32"'],
      },
      'Matte Poster - Unframed': {
        frames: ['No Frame'],
        sizes: ['12x16"', '18x24"', '24x32"', '30x40"'],
      },
    },

    upload: {
      maxBytes: 20 * 1024 * 1024,
      validMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
      validExtensions: ['jpg', 'jpeg', 'png', 'webp'],
    },
  };

  const state = {
    initializedPickers: new WeakSet(),
    initializedForms: new WeakSet(),
    defaultedPickers: new WeakSet(),
    previewUrls: new WeakMap(),
    observer: null,
  };

  /* ---------------------------------------------------------
   * Helpers
   * ------------------------------------------------------- */

  const normalize = (value) => String(value ?? '').trim();

  function isMainProductPicker(picker) {
    return (
      picker.matches('variant-picker[data-template-product-match="true"]') ||
      picker.matches('variant-selects[data-template-product-match="true"]') ||
      (!document.querySelector('[data-template-product-match="true"]') &&
        picker.closest('.product-information, .product-details, main'))
    );
  }

  function getMainPickers(root = document) {
    const modern = [...root.querySelectorAll('variant-picker, variant-selects, variant-radios')];
    return modern.filter(isMainProductPicker);
  }

  function getOptionInputs(picker, optionName) {
    return [...picker.querySelectorAll('input[type="radio"][data-option-name]')]
      .filter((input) => normalize(input.dataset.optionName) === optionName);
  }

  function getOptionSelect(picker, optionName) {
    const exact = [...picker.querySelectorAll('select')].find(
      (select) => normalize(select.name) === `options[${optionName}]`
    );
    if (exact) return exact;

    return [...picker.querySelectorAll('select')].find((select) =>
      [...select.options].some(
        (option) => normalize(option.dataset.optionName) === optionName
      )
    );
  }

  function getOptionContainer(picker, optionName) {
    const radio = getOptionInputs(picker, optionName)[0];
    if (radio) return radio.closest('.variant-option, fieldset, .product-form__input');

    const select = getOptionSelect(picker, optionName);
    if (select) return select.closest('.variant-option, .product-form__input, div');

    return null;
  }

  function getSelectedValue(picker, optionName) {
    const radios = getOptionInputs(picker, optionName);
    const checked = radios.find((input) => input.checked);
    if (checked) return normalize(checked.value);

    const select = getOptionSelect(picker, optionName);
    return select ? normalize(select.value) : '';
  }

  function setSelectedValue(picker, optionName, value, fireChange = true) {
    let changed = false;
    const radios = getOptionInputs(picker, optionName);

    if (radios.length) {
      const target = radios.find((input) => normalize(input.value) === value);
      if (target && !target.disabled) {
        changed = !target.checked;
        target.checked = true;
        target.dataset.currentChecked = 'true';

        radios.forEach((input) => {
          if (input !== target) input.dataset.currentChecked = 'false';
        });

        if (changed && fireChange) {
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      return changed;
    }

    const select = getOptionSelect(picker, optionName);
    if (select) {
      const target = [...select.options].find(
        (option) => normalize(option.value) === value && !option.disabled
      );
      if (target) {
        changed = select.value !== value;
        select.value = value;
        if (changed && fireChange) {
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    return changed;
  }

  function setOptionValidity(picker, optionName, validValues) {
    const valid = new Set(validValues);

    getOptionInputs(picker, optionName).forEach((input) => {
      const isValid = valid.has(normalize(input.value));
      const label = input.closest('label');

      if (!isValid) {
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
        input.dataset.pawpConditionDisabled = 'true';
        label?.classList.add('pawp-variant-invalid');
      } else if (input.dataset.pawpConditionDisabled === 'true') {
        input.disabled = false;
        input.removeAttribute('aria-disabled');
        delete input.dataset.pawpConditionDisabled;
        label?.classList.remove('pawp-variant-invalid');
      }
    });

    const select = getOptionSelect(picker, optionName);
    if (select) {
      [...select.options].forEach((option) => {
        const isValid = valid.has(normalize(option.value));

        if (!isValid) {
          option.disabled = true;
          option.dataset.pawpConditionDisabled = 'true';
        } else if (option.dataset.pawpConditionDisabled === 'true') {
          option.disabled = false;
          delete option.dataset.pawpConditionDisabled;
        }
      });
    }
  }

  function setFrameVisibility(picker, hide) {
    const container = getOptionContainer(picker, CONFIG.optionNames.frame);
    if (!container) return;

    container.classList.toggle('pawp-frame-hidden', hide);
    container.setAttribute('aria-hidden', hide ? 'true' : 'false');
  }

  // Hide placeholder variants that resolve to a true $0.00 Shopify variant.
  // Horizon recalculates product_option_value.variant as selections change, so
  // this stays aligned with the currently resolvable combinations.
  function setZeroPriceVisibility(picker) {
    picker.querySelectorAll('input[type="radio"][data-variant-price]').forEach((input) => {
      const isZeroPrice = Number(input.dataset.variantPrice) === 0;
      const label = input.closest('label');

      label?.classList.toggle('pawp-zero-price-hidden', isZeroPrice);

      if (isZeroPrice) {
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
        input.dataset.pawpZeroPriceDisabled = 'true';
      } else if (input.dataset.pawpZeroPriceDisabled === 'true') {
        delete input.dataset.pawpZeroPriceDisabled;
        if (input.dataset.pawpConditionDisabled !== 'true') {
          input.disabled = false;
          if (input.dataset.optionAvailable !== 'false') input.removeAttribute('aria-disabled');
        }
      }
    });

    picker.querySelectorAll('option[data-variant-price]').forEach((option) => {
      const isZeroPrice = Number(option.dataset.variantPrice) === 0;
      option.hidden = isZeroPrice;

      if (isZeroPrice) {
        option.disabled = true;
        option.dataset.pawpZeroPriceDisabled = 'true';
      } else if (option.dataset.pawpZeroPriceDisabled === 'true') {
        delete option.dataset.pawpZeroPriceDisabled;
        if (option.dataset.pawpConditionDisabled !== 'true') option.disabled = false;
      }
    });
  }

  /* ---------------------------------------------------------
   * Variant logic
   * ------------------------------------------------------- */

  function applyRules(picker, { resetDependents = false } = {}) {
    const format = getSelectedValue(picker, CONFIG.optionNames.format);
    const rule = CONFIG.rules[format];
    if (!rule) return;

    setOptionValidity(picker, CONFIG.optionNames.frame, rule.frames);
    setOptionValidity(picker, CONFIG.optionNames.size, rule.sizes);

    const unframed = format === 'Matte Poster - Unframed';
    setFrameVisibility(picker, unframed);

    const currentFrame = getSelectedValue(picker, CONFIG.optionNames.frame);
    const currentSize = getSelectedValue(picker, CONFIG.optionNames.size);

    const frameNeedsReset = resetDependents || !rule.frames.includes(currentFrame);
    const sizeNeedsReset = resetDependents || !rule.sizes.includes(currentSize);

    // Update both controls first, then fire native change events.
    if (frameNeedsReset) {
      setSelectedValue(picker, CONFIG.optionNames.frame, rule.frames[0], false);
    }
    if (sizeNeedsReset) {
      setSelectedValue(picker, CONFIG.optionNames.size, rule.sizes[0], false);
    }

    // Let Horizon process the final valid combination through its own event system.
    if (frameNeedsReset) {
      const radios = getOptionInputs(picker, CONFIG.optionNames.frame);
      const checked = radios.find((input) => input.checked);
      const select = getOptionSelect(picker, CONFIG.optionNames.frame);
      (checked || select)?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (sizeNeedsReset) {
      const radios = getOptionInputs(picker, CONFIG.optionNames.size);
      const checked = radios.find((input) => input.checked);
      const select = getOptionSelect(picker, CONFIG.optionNames.size);
      (checked || select)?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function applyDefaultVariant(picker) {
    if (state.defaultedPickers.has(picker)) return;

    // Mark first so Horizon re-renders cannot cause an endless default-reset loop.
    state.defaultedPickers.add(picker);

    setSelectedValue(picker, CONFIG.optionNames.format, CONFIG.defaults.format, false);
    applyRules(picker, { resetDependents: true });

    // One final format change lets Horizon resolve the complete selected variant.
    const formatRadios = getOptionInputs(picker, CONFIG.optionNames.format);
    const formatControl =
      formatRadios.find((input) => input.checked) ||
      getOptionSelect(picker, CONFIG.optionNames.format);

    formatControl?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function initPicker(picker) {
    if (!picker || state.initializedPickers.has(picker)) return;
    state.initializedPickers.add(picker);

    picker.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      const optionName =
        normalize(target.dataset?.optionName) ||
        normalize(target.name).replace(/^options\[(.*)\]$/, '$1');

      if (optionName === CONFIG.optionNames.format) {
        // Format changes always reset frame + size to first valid values.
        queueMicrotask(() => {
          applyRules(picker, { resetDependents: true });
          setZeroPriceVisibility(picker);
        });
      } else {
        queueMicrotask(() => {
          applyRules(picker);
          setZeroPriceVisibility(picker);
        });
      }

      queueMicrotask(syncAllAddButtons);
    });

    applyDefaultVariant(picker);
    applyRules(picker);
    setZeroPriceVisibility(picker);
  }

  /* ---------------------------------------------------------
   * Customer photo upload
   * ------------------------------------------------------- */

  function getMainProductForm() {
    const candidates = [
      ...document.querySelectorAll(
        '.product-details product-form-component form.shopify-product-form, ' +
        '.product-information product-form-component form.shopify-product-form, ' +
        'main product-form-component form.shopify-product-form'
      ),
    ];

    return candidates.find((form) => !form.closest('quick-add-component, quick-add-modal')) || null;
  }

  function getPhotoInput(form = getMainProductForm()) {
    if (!form) return null;

    // The styled PawpStarz upload lives outside Horizon's buy-button form.
    // Do NOT require input.form === form before finding it: on first boot the
    // Liquid-rendered form target can be stale during a section re-render.
    // Requiring that match was what caused a new fallback uploader to be
    // injected on every MutationObserver pass.
    const inputs = [
      ...document.querySelectorAll(
        '.product-details .pawp-photo-upload__input[type="file"][name="properties[Pet photo]"], ' +
        '.product-information .pawp-photo-upload__input[type="file"][name="properties[Pet photo]"], ' +
        '.product-details input[type="file"][name="properties[Pet photo]"], ' +
        '.product-information input[type="file"][name="properties[Pet photo]"]'
      ),
    ];

    return (
      inputs.find((input) => input.form === form) ||
      inputs.find((input) => input.closest('.pawp-photo-upload')) ||
      null
    );
  }

  function isValidPhoto(file) {
    if (!file) {
      return {
        valid: false,
        message: 'Please upload your pet photo before adding this item to your bag.',
      };
    }

    const extension = normalize(file.name).split('.').pop().toLowerCase();
    const validMimeTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/heic',
      'image/heif',
    ];
    const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
    const typeOkay = validMimeTypes.includes(file.type) || validExtensions.includes(extension);

    if (!typeOkay) {
      return {
        valid: false,
        message: 'Please choose a JPG, PNG, WEBP, or HEIC image.',
      };
    }

    if (file.size > CONFIG.upload.maxBytes) {
      return {
        valid: false,
        message: 'That photo is over 20MB. Please choose a smaller image.',
      };
    }

    return { valid: true, message: '' };
  }

  function getUploadErrorElement(input) {
    const fallbackWrap = input.closest('.pawp-upload-field');
    if (fallbackWrap) return fallbackWrap.querySelector('.pawp-error');

    const nativeWrap = input.closest('.pawp-photo-upload');
    if (!nativeWrap) return null;

    let error = nativeWrap.querySelector('.pawp-photo-upload__error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'pawp-photo-upload__error pawp-error';
      error.setAttribute('role', 'alert');
      error.hidden = true;
      const control = nativeWrap.querySelector('.pawp-photo-upload__control') || nativeWrap;
      control.appendChild(error);
    }
    return error;
  }

  function showUploadError(input, message) {
    if (!input) return;
    const error = getUploadErrorElement(input);
    const nativeWrap = input.closest('.pawp-photo-upload');
    const fallbackWrap = input.closest('.pawp-upload-field');

    nativeWrap?.classList.toggle('pawp-has-error', Boolean(message));
    fallbackWrap?.classList.toggle('pawp-has-error', Boolean(message));

    if (error) {
      error.textContent = message || '';
      error.hidden = !message;
    }
  }

  function syncAllAddButtons() {
    document.querySelectorAll(
      '.product-details product-form-component form.shopify-product-form, ' +
      '.product-information product-form-component form.shopify-product-form'
    ).forEach((form) => {
      if (form.closest('quick-add-component, quick-add-modal')) return;

      const photoInput = getPhotoInput(form);
      // Products without a PawpStarz photo field should keep Horizon's normal
      // add-to-cart behavior. When the field exists, require a valid upload.
      const photoOkay = !photoInput || Boolean(photoInput.files?.[0] && isValidPhoto(photoInput.files[0]).valid);
      const variantInput = form.querySelector('input[name="id"]');
      const variantOkay = Boolean(variantInput?.value) && !variantInput?.disabled;

      form.querySelectorAll('button[type="submit"][name="add"], .add-to-cart-button').forEach((button) => {
        button.disabled = !(photoOkay && variantOkay);
        button.toggleAttribute('aria-disabled', button.disabled);
        button.dataset.pawpPhotoRequired = photoOkay ? 'satisfied' : 'required';
      });
    });
  }

  function initPersonalizationForm(form) {
    if (!form) return;

    if (!form.id) {
      form.id = `pawp-product-form-${Math.random().toString(36).slice(2, 9)}`;
    }

    // Explicitly mark the product form as multipart. Horizon submits with
    // FormData, but this also keeps normal browser submission file-safe.
    form.setAttribute('enctype', 'multipart/form-data');
    form.setAttribute('encoding', 'multipart/form-data');

    // Remove any duplicate uploaders left behind by the broken fallback loop.
    // A fresh page load should not have these, but this makes theme-editor
    // hot reloads self-healing too.
    document.querySelectorAll('.pawp-upload-field--fallback').forEach((fallback) => fallback.remove());

    const fileInput = getPhotoInput(form);
    if (!fileInput) {
      syncAllAddButtons();
      return;
    }

    // Keep the ONE visible PawpStarz upload attached to the current Horizon form after any
    // section/variant re-render.
    fileInput.setAttribute('form', form.id);
    fileInput.name = 'properties[Pet photo]';
    fileInput.required = true;
    fileInput.setAttribute('aria-required', 'true');

    if (fileInput.dataset.pawpUploadValidationBound !== 'true') {
      fileInput.dataset.pawpUploadValidationBound = 'true';
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        const validation = isValidPhoto(file);

        if (!validation.valid && file) {
          showUploadError(fileInput, validation.message);
          fileInput.value = '';
        } else {
          showUploadError(fileInput, file ? '' : validation.message);
        }

        syncAllAddButtons();
      });
    }

    if (form.dataset.pawpPhotoSubmitBound !== 'true') {
      form.dataset.pawpPhotoSubmitBound = 'true';
      form.addEventListener(
        'submit',
        (event) => {
          const activeInput = getPhotoInput(form);
          const file = activeInput?.files?.[0];
          const validation = isValidPhoto(file);

          if (!validation.valid) {
            event.preventDefault();
            event.stopImmediatePropagation();
            showUploadError(activeInput, validation.message);
            syncAllAddButtons();
            activeInput?.focus();
            activeInput?.closest('.pawp-photo-upload, .pawp-upload-field')?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        },
        true
      );
    }

    state.initializedForms.add(form);
    syncAllAddButtons();
  }

  /* ---------------------------------------------------------
   * Styling
   * ------------------------------------------------------- */

  function injectStyles() {
    if (document.getElementById('pawp-product-customizer-styles')) return;

    const style = document.createElement('style');
    style.id = 'pawp-product-customizer-styles';
    style.textContent = `
      .pawp-frame-hidden {
        display: none !important;
      }

      /* Invalid and $0 placeholder combinations should never be shopper-facing. */
      .pawp-variant-invalid,
      .pawp-zero-price-hidden {
        display: none !important;
      }

      .pawp-personalization {
        display: grid;
        gap: 1rem;
        width: 100%;
        margin-block: 1rem 1.25rem;
      }

      .pawp-field {
        display: grid;
        gap: .45rem;
      }

      .pawp-label {
        font: inherit;
        font-weight: 600;
        line-height: 1.3;
      }

      .pawp-required {
        font-weight: 400;
        opacity: .72;
      }

      .pawp-file-input,
      .pawp-text-input,
      .pawp-textarea {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        color: var(--color-input-text, inherit);
        background: var(--color-input-background, transparent);
        border: var(--style-border-width-inputs, 1px) solid var(--color-input-border, currentColor);
        border-radius: var(--style-border-radius-inputs, .4rem);
        padding: var(--input-padding, .8rem);
        font: inherit;
      }

      .pawp-file-input {
        padding: .65rem;
      }

      .pawp-file-input::file-selector-button {
        appearance: none;
        border: 0;
        border-radius: calc(var(--style-border-radius-inputs, .4rem) * .75);
        padding: .6rem .8rem;
        margin-inline-end: .8rem;
        font: inherit;
        font-weight: 600;
        cursor: pointer;
      }

      .pawp-textarea {
        min-height: var(--input-textarea-min-height, 90px);
        resize: vertical;
      }

      .pawp-help,
      .pawp-error {
        margin: 0;
        font-size: .875em;
        line-height: 1.35;
      }

      .pawp-help {
        opacity: .68;
      }

      .pawp-error {
        color: #b42318;
      }

      .pawp-has-error .pawp-file-input {
        border-color: #b42318 !important;
        box-shadow: 0 0 0 1px #b42318;
      }

      .pawp-upload-preview {
        display: grid;
        grid-template-columns: 72px minmax(0, 1fr);
        gap: .75rem;
        align-items: center;
        padding: .7rem;
        border: 1px solid rgb(var(--color-foreground-rgb, 0 0 0) / .14);
        border-radius: var(--style-border-radius-inputs, .4rem);
      }

      .pawp-upload-preview[hidden] {
        display: none !important;
      }

      .pawp-preview-image {
        width: 72px;
        height: 72px;
        object-fit: cover;
        border-radius: calc(var(--style-border-radius-inputs, .4rem) * .8);
      }

      .pawp-preview-meta {
        min-width: 0;
        display: grid;
        gap: .3rem;
        justify-items: start;
      }

      .pawp-preview-name {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: .9em;
      }

      .pawp-remove-photo {
        appearance: none;
        border: 0;
        padding: 0;
        background: none;
        color: inherit;
        font: inherit;
        font-size: .9em;
        text-decoration: underline;
        text-underline-offset: .18em;
        cursor: pointer;
      }
    `;

    document.head.appendChild(style);
  }

  /* ---------------------------------------------------------
   * Boot / Horizon re-render support
   * ------------------------------------------------------- */

  function boot() {
    injectStyles();

    getMainPickers().forEach((picker) => {
      initPicker(picker);
      setZeroPriceVisibility(picker);
    });

    const form = getMainProductForm();
    if (form) initPersonalizationForm(form);

    syncAllAddButtons();
  }

  function observeHorizonRenders() {
    if (state.observer) return;

    let scheduled = false;
    state.observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        boot();
      });
    });

    state.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      observeHorizonRenders();
    });
  } else {
    boot();
    observeHorizonRenders();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('variant:changed', () => {
    getMainPickers().forEach((picker) => {
      applyRules(picker);
      setZeroPriceVisibility(picker);
    });
    syncAllAddButtons();
  });
})();
